/* eslint frontbucket-patterns/no-new-sagas: "warn" */
import { Buffer } from 'buffer';
import { gunzipSync } from 'zlib';

import qs from 'qs';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { getCurrentRepository } from 'src/selectors/repository-selectors';
import authRequest from 'src/utils/fetch';

import { EMPTY_STEP } from '../../constants';
import { Step } from '../../models';
import envBaseUrl from '../../utils/env-base-url';
import { getFeatureFlagFromStep } from '../../utils/getFeatureFlagFromStep';
import {
  capturePipelinesExceptionWithTags,
  createErrorMessage,
} from '../../utils/sentry';
import { METRIC_UPDATED, REQUEST_METRICS } from '../actions/pipelines';

const AGENT_STEP_METRICS_ENABLED = 'agent_step-metrics-enabled';

export const getMetricsUrl = (
  repositoryFullSlug: string,
  pipelineUuid: string,
  stepUuid: string,
  queryParams: {} = {}
) => {
  const baseUrl = `${envBaseUrl(
    '/!api/internal'
  )}/repositories/${repositoryFullSlug}/pipelines/${pipelineUuid}/steps/${stepUuid}/metrics`;

  if (Object.keys(queryParams).length === 0) {
    return `${baseUrl}/content`;
  }

  const allQueryParams = {
    pagelen: 100,
    ...queryParams,
  };

  return `${baseUrl}?${qs.stringify(allQueryParams, { allowDots: true })}`;
};

export function processMetrics(data: ArrayBuffer): any {
  try {
    const buffer = Buffer.from(data);
    const decompressedData = gunzipSync(buffer);
    const decodedDecompressedData = new TextDecoder().decode(decompressedData);

    const jsonMatch = decodedDecompressedData.match(/{.*}/s);
    if (!jsonMatch) {
      throw new Error('Failed to extract JSON from decompressed data');
    }

    const jsonData = JSON.parse(jsonMatch[0]);
    return jsonData.values;
  } catch (error) {
    throw new Error(`Failed to process metrics data: ${error.message}`);
  }
}

export function* requestMetricsSaga(action: {
  meta: { pipelineUuid: string; step: Step };
  type: string;
}): Generator<any, any, any> {
  const meta = action.meta || action; // websocket is not grouping params in action
  if (!meta.pipelineUuid || !meta.step || meta.step.uuid === EMPTY_STEP) {
    return;
  }
  const { pipelineUuid, step } = meta;
  const stepUuid = step.uuid;
  const { full_name } = yield select(getCurrentRepository);
  const agentStepMetricsEnabled = getFeatureFlagFromStep(
    step.featureFlags,
    AGENT_STEP_METRICS_ENABLED
  );

  try {
    if (agentStepMetricsEnabled) {
      const res = yield call(
        fetch,
        authRequest(getMetricsUrl(full_name, pipelineUuid, stepUuid))
      );

      if (!res.ok) {
        throw new Error(yield createErrorMessage(res));
      }

      const arrayBuffer = yield call([res, 'arrayBuffer']);
      const payload = processMetrics(arrayBuffer);

      yield put({
        type: REQUEST_METRICS.SUCCESS,
        meta: action.meta,
        payload,
      });
    } else {
      const res = yield all([
        call(
          fetch,
          authRequest(
            getMetricsUrl(full_name, pipelineUuid, stepUuid, {
              metric_type: 'CPU',
            })
          )
        ),
        call(
          fetch,
          authRequest(
            getMetricsUrl(full_name, pipelineUuid, stepUuid, {
              metric_type: 'MEMORY',
            })
          )
        ),
        call(
          fetch,
          authRequest(
            getMetricsUrl(full_name, pipelineUuid, stepUuid, {
              metric_type: 'MEMORY_PERCENTAGE',
            })
          )
        ),
      ]);

      for (const response of res) {
        if (!response.ok) {
          throw new Error(yield createErrorMessage(response));
        }
      }

      const data = yield all(res.map((r: any) => call([r, 'json'])));

      yield put({
        type: REQUEST_METRICS.SUCCESS,
        meta: action.meta,
        payload: data
          .reduce((reducer: any, r: { values: any[] }) => {
            // @ts-ignore use es2019 lib
            reducer.push(r.values.flat());
            return reducer;
          }, [])
          .flat(),
      });
    }
  } catch (e) {
    capturePipelinesExceptionWithTags(e, {
      segment: REQUEST_METRICS.ERROR,
    });
    yield put({
      type: REQUEST_METRICS.ERROR,
      meta: action.meta,
      payload: e,
    });
  }
}

export default function* () {
  yield all([
    takeLatest(REQUEST_METRICS.REQUEST, requestMetricsSaga),
    takeLatest(METRIC_UPDATED, requestMetricsSaga),
  ]);
}
